<template>
  <ModalStep
    id="edit-user-modal-content"
    :header="{
      title: t('admin.users.editUserModal.title'),
    }"
  >
    <template #bodyContent>
      <div class="user-edit-form" v-if="userRef">
        <div class="user-info">
          <h3>
            <strong>{{ userRef.firstName || '' }} {{ userRef.lastName || '' }}</strong>
          </h3>
        </div>

        <div class="email-field">
          <CustomInput
            type="email"
            id="user-email"
            data-test-id="user-email-input"
            :label="`${t('ui.input.email.label')} *`"
            :placeholder="t('ui.input.email.placeholder')"
            :value="emailRef"
            :errors="emailErrorRef"
            @emit:change="emailChangeHandler"
          />
          <p class="field-hint">{{ t('admin.users.editUserModal.email.hint') }}</p>
        </div>

        <CustomSelect
          ref="roleSelectRef"
          id="user-role"
          data-test-id="user-role-select"
          :options="roleOptionsRef"
          :label="`${t('ui.input.role.label')} *`"
          @emit:change="roleChangeHandler"
          :value="[]"
        />
        <p v-if="isSuperAdminRef" class="field-hint">{{ t('admin.users.editUserModal.superAdminHint') }}</p>

        <div class="roles-container">
          <div class="selected-roles flex flex-wrap gap-2" v-if="rolesRef.length">
            <Badge
              v-for="role in rolesRef.filter((r) => !!r)"
              :key="role"
              variant="info"
              class="flex gap-1 items-center justify-center"
              :data-test-id="isLockedRole(role) ? 'edit-user-role-locked' : undefined"
            >
              <span>
                {{ userRolesNamesMap[role] ? t(userRolesNamesMap[role]) : role }}
              </span>
              <button
                v-if="!isLockedRole(role)"
                type="button"
                @click="removeRole(role)"
                class="ml-3 text-red-600 hover:text-red-800 text-lg font-bold"
              >
                ×
              </button>
            </Badge>
          </div>
        </div>
      </div>

      <div v-else-if="isLoadingRef" class="loading">
        {{ t('common.loading') }}
      </div>

      <div v-else class="error">
        {{ t('error.userNotFound') }}
      </div>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton usage="button" type="button" color="white" :text="t('ui.button.cancel')" @emit:click="onClose" />
        <CustomButton
          :is-disabled="!rolesRef.length || !!emailErrorRef || isUpdatingRef"
          :is-loading="isUpdatingRef"
          usage="button"
          type="button"
          data-test-id="user-edit-submit-button"
          color="primary"
          :text="t('admin.users.editUserModal.save')"
          @emit:click="onSubmit"
        />
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, nextTick, ref, watch } from 'vue'
import { z } from 'zod'

import Badge from '@/components/Badge.vue'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import { updateUserEmailInProgramMutation, updateUserRolesInProgramMutation } from '@/features/programs/api'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomInput from '@/features/ui/components/input/custom-input.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import CustomSelect from '@/features/ui/components/select/custom-select.vue'
import { selectGetEditProfileModal } from '@/features/ui/store/modal/modal.selectors'
import type { ConfirmActionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useUser from '@/features/users/hooks/use-user.hook'
import { userRolesNamesMap } from '@/features/users/utils'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { invalidateAdminUsers, invalidateUser } from '@/utils/lib/vue-query'

const toast = useToast()
const { t } = useI18n()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const editProfileModalRef = selectGetEditProfileModal(modalsStore)

// Get userId from modal attached data
const userIdRef = computed(() => editProfileModalRef.value?.attachedData?.userId)
const { userRef } = useUser(userIdRef)
const rolesRef = ref<RoleEnumTypeV2[]>([])
const isLoadingRef = ref(false)

// set the roles when the userRef is updated
watch(
  () => userRef.value?.roles,
  (newRoles) => {
    if (Array.isArray(newRoles)) {
      rolesRef.value = [...newRoles]
    } else {
      rolesRef.value = []
    }
  },
  { immediate: true },
)

// KC-602 : l'email est prérempli depuis la ligne de la liste (le détail user
// ne renvoie l'email qu'à son propriétaire)
const initialEmailRef = computed(() => editProfileModalRef.value?.attachedData?.email ?? '')
const emailRef = ref(initialEmailRef.value)
const isEmailAlreadyUsedRef = ref(false)
const isEmailReservedRef = ref(false)

watch(initialEmailRef, (email) => {
  emailRef.value = email
  isEmailAlreadyUsedRef.value = false
  isEmailReservedRef.value = false
})

const normalizedEmailRef = computed(() => emailRef.value.trim().toLowerCase())
const isEmailChangedRef = computed(() => normalizedEmailRef.value !== initialEmailRef.value.toLowerCase())

const emailErrorRef = computed(() => {
  if (!z.string().email().safeParse(normalizedEmailRef.value).success) return t('error.invalidEmail')
  if (isEmailAlreadyUsedRef.value) return t('admin.users.editUserModal.email.alreadyUsed')
  if (isEmailReservedRef.value) return t('admin.users.editUserModal.email.reserved')
  return undefined
})

function emailChangeHandler(value: string) {
  emailRef.value = value
  isEmailAlreadyUsedRef.value = false
  isEmailReservedRef.value = false
}

// Un super admin (ADMIN + SUPPORT) se gère depuis « Ma plateforme » : ici, seuls
// mentor et mentoré se modifient (KC-653).
const isSuperAdminRef = computed(() => {
  const roles = userRef.value?.roles ?? []
  return roles.includes(RoleEnumTypeV2.ADMIN) && roles.includes(RoleEnumTypeV2.SUPPORT)
})
const participantRoles = [RoleEnumTypeV2.MENTOR, RoleEnumTypeV2.MENTEE]

function isLockedRole(role: RoleEnumTypeV2) {
  return isSuperAdminRef.value && !participantRoles.includes(role)
}

const roleOptionsRef = computed(() =>
  [
    { value: RoleEnumTypeV2.MENTOR, label: t('common.mentor.capitalize') },
    { value: RoleEnumTypeV2.MENTEE, label: t('common.mentee.capitalize') },
    { value: RoleEnumTypeV2.MANAGER, label: t('common.manager.capitalize') },
    { value: RoleEnumTypeV2.ADMIN, label: t('common.admin.capitalize') },
  ].filter((option) => !isLockedRole(option.value)),
)

const roleSelectRef = ref<any>(null)

function roleChangeHandler(value: RoleEnumTypeV2 | RoleEnumTypeV2[]) {
  // Don't process empty values to avoid infinite loops
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return
  }

  if (Array.isArray(value)) {
    // Filter out empty values
    rolesRef.value = value.filter((v) => !!v)
  } else if (value) {
    const idx = rolesRef.value.indexOf(value)
    if (idx === -1) {
      rolesRef.value.push(value)
    }
    // Clear the select input value after selection
    // Use nextTick to avoid triggering change event immediately
    nextTick(() => {
      if (roleSelectRef.value) {
        if (typeof roleSelectRef.value.clear === 'function') {
          roleSelectRef.value.clear()
        } else {
          roleSelectRef.value.value = null
        }
      }
    })
  }
}

function removeRole(role: RoleEnumTypeV2) {
  const idx = rolesRef.value.indexOf(role)
  if (idx !== -1) {
    rolesRef.value.splice(idx, 1)
  }
}

const { mutate: updateUser, isPending: isUpdatingRef } = useMutation({
  mutationFn: async ({ userId, programId }: { userId: string; programId: string }) => {
    // L'email d'abord : s'il est refusé (adresse déjà prise), rien n'est modifié
    if (isEmailChangedRef.value) {
      await updateUserEmailInProgramMutation({ userId, programId, email: normalizedEmailRef.value })
    }
    await updateUserRolesInProgramMutation({ userId, programId, roles: rolesRef.value })
  },
  onError: (error: { status?: number; response?: { code?: string } }) => {
    if (error.response?.code === 'SUPER_ADMIN_ROLES_LOCKED') {
      toast?.error(t('admin.users.editUserModal.rolesLocked'))
      return
    }
    if (error.status === 409) {
      isEmailAlreadyUsedRef.value = true
      return
    }
    if (error.status === 403 && error.response?.code === 'CONSTEL_STAFF_EMAIL_RESERVED') {
      isEmailReservedRef.value = true
      return
    }
    console.error('Error updating user:', error)
    toast?.error(t('error.common'))
  },
  onSuccess: async () => {
    await invalidateAdminUsers(selectedProgramIdRef.value!)
    // if we invalide the current users, we need to invalidate the current user
    await invalidateUser(userIdRef.value!)
    toast?.success(t('admin.users.editUserModal.success'))
    modalsStore.toggleModal('edit-profile', false)
  },
})

async function onSubmit() {
  if (!userIdRef.value || !selectedProgramIdRef.value) return

  modalsStore.toggleModal('confirm-action', true)
  modalsStore.updateModalAttachedData<ConfirmActionModalType>('confirm-action', {
    title: t('admin.users.editUserModal.confirmation.title', {
      userName: `${userRef.value?.firstName || ''} ${userRef.value?.lastName || ''}`.trim(),
    }),
    type: 'danger',
    fn: async () => {
      updateUser({
        userId: userIdRef.value!,
        programId: selectedProgramIdRef.value!,
      })
    },
  })
}

function onClose() {
  modalsStore.toggleModal('edit-profile', false)
}
</script>

<style lang="scss">
@import './edit-user-modal-content.scss';
</style>
